/**
 * CrmProposalEditor — full proposal authoring form (all inputs), schema-driven from
 * PROPOSAL_INPUT_GROUPS. Writes inputs to the Fee Proposals table; Airtable computes the fees, which
 * we re-read and show in a readout after save (write-then-reread). Ratings are inherited from the
 * Opportunity (shown read-only). Preview/PDF/post come in 7/8.
 *
 * Takeoff integration (Phase 3): any category with ≥1 takeoff region (summed across the
 * opportunity's drawings) renders LOCKED — the field shows the cross-drawing takeoff total and the
 * save path overrides the value with that total, so the Airtable write always carries the takeoff
 * number. Deleting all of a category's regions returns the field to manual entry. "Take off from
 * drawings" opens a launcher over the opportunity's drawings → TakeoffEditor. Creating a new
 * proposal offers to copy-forward the most recent prior proposal's regions.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import CrmModal, { F } from './CrmModal.jsx'
import { createProposal, updateProposal, getProposalDetail, toProposalFields, previewProposalPdf, previewProposalHtml, getProposalsForOpportunity, PROPOSAL_INPUT_GROUPS, HAS_WEB_VIEW } from '../../services/proposals.js'
import { alertDialog, confirmDialog } from '../Dialog.jsx'
import { getFeeModels } from '../../services/feeModels.js'
import { searchContacts, getContactNamesByIds } from '../../services/contacts.js'
import AllInclusiveFeePanel from './AllInclusiveFeePanel.jsx'
import ContractTermsPanel from './ContractTermsPanel.jsx'
import AllotmentScopePanel from './AllotmentScopePanel.jsx'
import { getContractTerms, saveExcludedMetrics } from '../../services/proposalContractTerms.js'
import TakeoffEditor from '../takeoff/TakeoffEditor.jsx'
import { listDrawings, listRegions, computeTakeoffSums, copyRegions, drawingTypeLabel } from '../../services/takeoffs.js'
import { categoryColor, fmtValue } from '../takeoff/takeoffGeom.js'

/** A 'pct' form value (whole percentage, e.g. 90) back to the fraction Airtable stores (0.9).
 *  Returns null for blank/absent so a missing adjustment reads as "none", not as zero. */
function pctFrac(v) {
  if (v === '' || v == null) return null
  const n = Number(v)
  return isNaN(n) ? null : n / 100
}

function money(n) { return n ? '$' + Math.round(n).toLocaleString() : '—' }
function pct(n) { return n == null ? '—' : (Math.round(n * 1000) / 10) + '%' }
function round(n, dp) { const m = Math.pow(10, dp); return Math.round(n * m) / m }

// seedFrom: an existing (posted) proposal whose authoring inputs pre-fill a brand-new record —
// used by the "Replace" flow (CrmProposalDetailModal.jsx) so a client's requested changes can be
// made on a fresh draft instead of the posted original. Only ever consulted when `proposal` is
// falsy (creating fresh) — a seeded editor still creates a NEW record on save, never updates
// seedFrom's own record. Only fields in PROPOSAL_INPUT_GROUPS ever get copied, which naturally
// excludes every posted/signed/journey field (those aren't authoring inputs at all).
/** A checkbox field normally arrives as `true` or absent. But `Long Distance Travel` was declared
 *  'yesno' until 2026-08 and that path writes the literal STRING 'Yes'/'No' — and `!!'No'` is
 *  `true`, which would show an unchecked value as checked and then SAVE it as checked, silently
 *  turning long-distance travel on. Normalize the string forms explicitly. */
const boolFromAirtable = v => (typeof v === 'string' ? /^(yes|true|1|checked)$/i.test(v.trim()) : !!v)

function initVals(proposal, initialType, seedFrom) {
  const raw = (proposal && proposal.raw) || (seedFrom && seedFrom.raw) || {}
  const v = {}
  for (const g of PROPOSAL_INPUT_GROUPS) for (const f of g.fields) {
    if (f.type === 'computed') continue   // read from computed.raw at render time instead
    const r = raw[f.key]
    if (f.type === 'bool') v[f.key] = boolFromAirtable(r)
    else if (f.type === 'multiselect') v[f.key] = Array.isArray(r) ? r : []
    else if (f.type === 'feemodel' || f.type === 'contact') v[f.key] = Array.isArray(r) ? (r[0] || '') : (r || '')
    // 'pct' fields are stored as a fraction (0.2 = 20%) — show the whole percentage the editor
    // reads/writes elsewhere (mirrors CrmFeeModelEditor.jsx's toDisplay/toStore pattern).
    else if (f.type === 'pct') v[f.key] = r != null ? round(r * 100, 4) : ''
    else v[f.key] = r != null ? r : ''
  }
  if (!proposal) {
    v['Fee Proposal Type'] = initialType || v['Fee Proposal Type'] || 'Basic Services'
    if (!v['Proposal Date']) { const d = new Date(); v['Proposal Date'] = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` }
    if (!v['Expiration Timeline']) v['Expiration Timeline'] = 14
  }
  return v
}

export default function CrmProposalEditor({ proposal, opportunityId, opportunityName, initialType, seedFrom, onClose, onSaved }) {
  const creating = !proposal
  const [vals, setVals] = useState(() => initVals(proposal, initialType, seedFrom))
  const propType = vals['Fee Proposal Type'] || initialType || 'Basic Services'
  const [feeModels, setFeeModels] = useState([])
  const [busy, setBusy] = useState(false)
  const [computed, setComputed] = useState(proposal || null)
  const loadedRef = useRef(false)
  // True until the on-open re-read lands (see the hydrate effect below). Saving is blocked while
  // it runs so a save can never race the read that is about to correct the form.
  const [hydrating, setHydrating] = useState(!!proposal)
  // The keys the user has actually edited in THIS editor session — the scope every update write
  // is diffed down to (toProposalFields' `only`). A ref, not state: it must never cause a render.
  const dirtyRef = useRef(new Set())

  // Area metrics left out of the fee (database/388). `excluded` = the keys ticked "Leave out of
  // fee" in the form; `excludedSavedRef` = the { key: number } map last read from / written to the
  // server. An excluded metric's Airtable field is blank (so the FPV ignores it), so on every
  // re-seed its number comes back from this map instead.
  const [excluded, setExcluded] = useState(() => new Set())
  const excludedSavedRef = useRef({})
  const excludedDirtyRef = useRef(false)

  // Takeoff state — regions are the source; sums/locks derive from them.
  const [regions, setRegions] = useState(null)              // null = not loaded yet
  const [takeoffOpen, setTakeoffOpen] = useState(false)     // drawings launcher modal
  const [drawings, setDrawings] = useState(null)            // null = not loaded yet
  const [editorDrawing, setEditorDrawing] = useState(null)  // drawing open in TakeoffEditor
  const regionsLoadedRef = useRef(false)
  const drawingsLoadedRef = useRef(false)

  // The proposal exists once saved — even while the modal is still in "create" mode.
  const savedId = (computed && computed.id) || (proposal && proposal.id) || null
  const oppId = opportunityId || (computed && computed.opportunityId) || (proposal && proposal.opportunityId) || null

  const sums = useMemo(() => computeTakeoffSums(regions || []), [regions])
  const regionCountByDrawing = useMemo(() => {
    const m = {}
    for (const r of regions || []) m[r.drawing_id] = (m[r.drawing_id] || 0) + 1
    return m
  }, [regions])

  useEffect(() => { if (loadedRef.current) return; loadedRef.current = true; getFeeModels().then(setFeeModels).catch(() => {}) }, [])

  // Lazy, non-blocking takeoff-sums load when opening an existing proposal.
  useEffect(() => {
    if (regionsLoadedRef.current || !proposal || !proposal.id) return
    regionsLoadedRef.current = true
    listRegions(proposal.id).then(setRegions).catch(() => setRegions([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function refreshRegions(id) {
    if (!id) return
    listRegions(id).then(setRegions).catch(() => {})
  }

  /** Re-seed the whole form from a freshly-read record, preserving any field the user has already
      edited (its dirty keys) so a slow read can never clobber typing already in progress. */
  function reseed(fresh) {
    if (!fresh || !fresh.raw) return
    setComputed(fresh)
    const next = initVals(fresh, initialType, null)
    for (const [k, n] of Object.entries(excludedSavedRef.current)) next[k] = n
    setVals(cur => {
      const merged = { ...next }
      for (const k of dirtyRef.current) merged[k] = cur[k]
      return merged
    })
  }

  // "Replace" seeds a new proposal from an old one's Airtable fields, where a left-out metric is
  // blank. Carry the old proposal's left-out metrics over too, or their footage would be lost.
  useEffect(() => {
    if (proposal || !seedFrom || !seedFrom.id) return
    let cancelled = false
    getContractTerms(seedFrom.id).then(t => {
      const m = (t && t.excludedMetrics) || {}
      if (cancelled || !Object.keys(m).length) return
      excludedDirtyRef.current = true
      setExcluded(new Set(Object.keys(m)))
      setVals(cur => {
        const next = { ...cur }
        for (const [k, n] of Object.entries(m)) if (!dirtyRef.current.has(k)) next[k] = n
        return next
      })
    }).catch(() => {})
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function toggleExcluded(key) {
    excludedDirtyRef.current = true
    dirtyRef.current.add(key)   // so the Airtable field is written (blanked, or the number put back)
    setExcluded(s => { const n = new Set(s); if (n.has(key)) n.delete(key); else n.add(key); return n })
  }

  // ALWAYS re-read the record when the editor opens. The `proposal` prop is a snapshot captured
  // when its row was clicked, and the list behind it never re-syncs that object — so re-opening
  // the editor after a save re-seeded the form from PRE-save values. Combined with a save that
  // wrote the entire field set, the next save then silently reverted everything the previous one
  // had changed (the "SD Adjustment keeps going back to 100%" report). The scoped write in save()
  // makes that harmless on its own; this makes what you are looking at correct too.
  useEffect(() => {
    if (!proposal || !proposal.id) { setHydrating(false); return }
    let cancelled = false
    // The left-out-of-fee map is read first: the reseed needs it to put those numbers back.
    getContractTerms(proposal.id)
      .then(t => {
        if (cancelled) return
        excludedSavedRef.current = (t && t.excludedMetrics) || {}
        if (!excludedDirtyRef.current) setExcluded(new Set(Object.keys(excludedSavedRef.current)))
      })
      .catch(() => {})
      .then(() => getProposalDetail(proposal.id))
      .then(fresh => { if (!cancelled) reseed(fresh) })
      .catch(() => {})
      .finally(() => { if (!cancelled) setHydrating(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function set(k, v) { dirtyRef.current.add(k); setVals(s => ({ ...s, [k]: v })) }

  async function save() {
    if (busy) return
    setBusy(true)
    try {
      // An UPDATE writes only the fields touched in this session, plus any takeoff-locked category
      // (whose sum lives outside the form and can have moved under it). A CREATE writes everything,
      // since there is no prior record for an untouched field to be diffed against.
      // Snapshotted, not read live: anything typed WHILE the save is in flight must stay dirty so
      // the post-save re-seed preserves it instead of silently discarding it.
      const dirtyAtSave = new Set(dirtyRef.current)
      const only = savedId ? new Set(dirtyAtSave) : null
      // Locked categories save the takeoff sum, never the (hidden) manual value.
      const effVals = { ...vals }
      for (const [key, s] of Object.entries(sums)) {
        if (s.count > 0 && key in effVals) { effVals[key] = s.total; if (only) only.add(key) }
      }
      // Left out of the fee: the number goes to Supabase for the document, the Airtable field is
      // written blank so the FPV never sees it. Supabase is written FIRST on an update, so a failed
      // save can leave the fee counting the metric for one more save, never lose the number.
      const excludedMap = {}
      for (const k of excluded) {
        const n = Number(effVals[k])
        if (effVals[k] !== '' && effVals[k] != null && Number.isFinite(n)) excludedMap[k] = n
        effVals[k] = ''
        if (only) only.add(k)
      }
      const excludedChanged = excludedDirtyRef.current
        || JSON.stringify(excludedMap) !== JSON.stringify(excludedSavedRef.current)
      if (savedId && excludedChanged) {
        const t = await saveExcludedMetrics(savedId, excludedMap)
        excludedSavedRef.current = (t && t.excludedMetrics) || excludedMap
        excludedDirtyRef.current = false
      }
      const fields = toProposalFields(effVals, only)
      let saved
      if (!savedId) {
        fields['Opportunity'] = [opportunityId]
        // 'Client Rating' (Fee Proposals) is a lookup sourced through the legacy 'Projects' link
        // field, not 'Opportunity' — a leftover from before 'Opportunity' was standardized. Set
        // both so the Client Rating Adjustment (part of Sum of Decision Factors) actually
        // resolves instead of silently computing to 0 for every new proposal. The correct
        // long-term fix is repointing that Airtable lookup to 'Opportunity' to match Complexity/
        // Portfolio Rating; this is a belt-and-suspenders fill until that's done.
        fields['Projects'] = [opportunityId]
        saved = await createProposal(fields)
        if (saved && Object.keys(excludedMap).length) {
          const t = await saveExcludedMetrics(saved.id, excludedMap)
          excludedSavedRef.current = (t && t.excludedMetrics) || excludedMap
          excludedDirtyRef.current = false
        }
        if (saved) { setRegions([]); await maybeCopyForward(saved.id) }
      } else if (only && only.size === 0) {
        saved = { id: savedId }   // nothing touched — skip the write, still re-read below
      } else saved = await updateProposal(savedId, fields)
      if (saved) {
        const fresh = await getProposalDetail(saved.id)   // reread computed fees
        // Retire only what this save actually carried — keys touched since stay dirty (and so
        // survive the re-seed below).
        for (const k of dirtyAtSave) dirtyRef.current.delete(k)
        const rec = fresh || (saved.raw ? saved : null)
        if (rec) reseed(rec)
        if (onSaved) onSaved(rec || saved)
      }
    } catch (e) {
      await alertDialog({ title: 'Save failed', message: (e && e.message) || 'Could not save the proposal.' })
    } finally { setBusy(false) }
  }

  /** Best-effort copy-forward: offer to duplicate the most recent prior proposal's
      takeoff regions onto the just-created one. Never blocks creation. */
  async function maybeCopyForward(newId) {
    try {
      if (!oppId) return
      const prior = (await getProposalsForOpportunity(oppId)).filter(p => p.id !== newId)
      if (!prior.length) return
      const prevRegions = await listRegions(prior[0].id)
      if (!prevRegions.length) return
      const ok = await confirmDialog({
        title: 'Copy takeoff?',
        message: `The most recent proposal has ${prevRegions.length} takeoff region${prevRegions.length === 1 ? '' : 's'} drawn on the drawings. Copy them to this proposal as a starting point?`,
        confirmLabel: 'Copy takeoff',
      })
      if (!ok) return
      await copyRegions({ fromProposalId: prior[0].id, toProposalId: newId })
      refreshRegions(newId)
    } catch (e) {
      await alertDialog({
        title: 'Takeoff copy failed',
        message: ((e && e.message) || 'The regions could not be copied.') + ' The proposal itself was created. You can draw takeoff regions from the drawings instead.',
      })
    }
  }

  async function openTakeoff() {
    if (!savedId) {
      await alertDialog({ title: 'Save first', message: 'Save the proposal before starting a takeoff. Regions are stored per proposal.' })
      return
    }
    setTakeoffOpen(true)
    if (!drawingsLoadedRef.current) {
      drawingsLoadedRef.current = true
      if (oppId) listDrawings(oppId).then(setDrawings).catch(() => setDrawings([]))
      else setDrawings([])
    }
  }

  const ratings = computed && computed.raw ? {
    complexity: (computed.raw['Complexity Rating'] || [])[0],
    portfolio: (computed.raw['Portfolio Rating'] || [])[0],
    client: (computed.raw['Client Rating'] || [])[0],
  } : {}

  async function preview() {
    const id = computed && computed.id
    if (!id) { await alertDialog({ title: 'Save first', message: 'Save the proposal before previewing the PDF.' }); return }
    const tab = window.open('', '_blank')
    try { const url = await previewProposalPdf(id, { working: true }); if (tab) tab.location = url; else window.open(url, '_blank') }
    catch (e) { if (tab) tab.close(); await alertDialog({ title: 'Preview failed', message: (e && e.message) || 'Could not generate the PDF.' }) }
  }

  async function previewWeb() {
    const id = computed && computed.id
    if (!id) { await alertDialog({ title: 'Save first', message: 'Save the proposal before previewing the web view.' }); return }
    const tab = window.open('', '_blank')
    try { const url = await previewProposalHtml(id, { working: true }); if (tab) tab.location = url; else window.open(url, '_blank') }
    catch (e) { if (tab) tab.close(); await alertDialog({ title: 'Preview failed', message: (e && e.message) || 'Could not generate the web view.' }) }
  }

  const footer = (
    <>
      <button style={F.btnGhost} onClick={onClose} disabled={busy}>Close</button>
      <button style={F.btnGhost} onClick={openTakeoff} disabled={busy}>Take off from drawings</button>
      {!creating && HAS_WEB_VIEW.has(propType) && <button style={F.btnGhost} onClick={previewWeb} disabled={busy}>Preview web view</button>}
      {!creating && <button style={F.btnGhost} onClick={preview} disabled={busy}>Preview PDF</button>}
      <button style={F.btnPrimary} onClick={save} disabled={busy || hydrating}>{hydrating ? 'Loading…' : busy ? 'Saving…' : (!savedId ? 'Create proposal' : 'Save')}</button>
    </>
  )

  return (
    <>
    <CrmModal title={creating ? `New proposal — ${opportunityName || ''}` : (computed?.name || 'Proposal')} onClose={onClose} footer={footer} width={820}>
      <div style={S.typeChip}>{propType}</div>
      {creating && seedFrom && (
        <div style={S.seedNote}>Pre-filled from {seedFrom.name || 'the replaced proposal'} — adjust below for the requested changes, then create to save as a new draft.</div>
      )}

      {/* Computed readout (write-then-reread) */}
      {computed && (
        <div style={S.readout}>
          <div style={S.readoutHead}>Computed fee {creating ? '' : '(saved)'}</div>
          <div style={S.readoutGrid}>
            <RO label="FPV" v={money(computed.fpv)} />
            <RO label="Weighted %" v={pct(computed.weightedFeePct)} />
            <RO label="Contract fee" v={money(computed.contractFee)} />
            <RO label="Pkg A / B / C" v={`${money(computed.packages.A)} / ${money(computed.packages.B)} / ${money(computed.packages.C)}`} />
            {(ratings.complexity || ratings.portfolio || ratings.client) &&
              <RO label="Ratings (from opp)" v={[ratings.complexity, ratings.portfolio, ratings.client].filter(Boolean).join(' · ')} />}
          </div>
        </div>
      )}

      {/* All-inclusive fixed fee — Basic Services only, and only once the record exists (the
          override is keyed by the proposal's own id). Off by default: with no override row the
          proposal prices from Airtable's engine exactly as it always has. */}
      {propType === 'Basic Services' && (
        <AllInclusiveFeePanel
          proposalId={savedId}
          proposalRaw={computed && computed.raw}
          // LIVE form values, not the saved record. The phase list and the four phase Adjustments
          // are both editable right below this panel, and a split computed against the saved copy
          // prices whatever was selected the last time someone hit Save — which is how a proposal
          // ended up pricing Interior Architecture, a phase it does not sell, while Discretionary
          // Approvals, a phase it does, got nothing. `vals` holds 'pct' fields as whole percentages
          // (90), so they are converted back to the fractions the engine uses.
          includedPhases={vals['Phases Included']}
          adjustments={{
            SD: pctFrac(vals['SD Adjustment']), DA: pctFrac(vals['DA Adjustment']),
            AD: pctFrac(vals['AD Adjustment']), IA: pctFrac(vals['IA Adjustment']),
          }}
          onChanged={() => { if (savedId) getProposalDetail(savedId).then(p => p && setComputed(p)).catch(() => {}) }}
        />
      )}

      {/* Negotiated wording in the agreement itself (Article 3.6's initial payment). Basic Services
          only — a Design Consultation prints its own terms and has no Article 3. Standard until
          someone changes it, like the panel above. */}
      {propType === 'Basic Services' && savedId && (
        <ContractTermsPanel
          proposalId={savedId}
          // The live form value: the dollar Retainer Amount and this percentage are two mutually
          // exclusive versions of the same clause, and the panel says which one will print.
          retainerAmount={vals['Retainer Amount']}
        />
      )}

      {/* Design Consultation: what the allotment covers, printed in the agreement. Live allotment
          amount so the panel says where the list will print. */}
      {propType === 'Design Consultation' && savedId && (
        <AllotmentScopePanel proposalId={savedId} allotmentAmount={vals['Allotment Amount']} />
      )}

      {PROPOSAL_INPUT_GROUPS.filter(g => !g.showFor || g.showFor.includes(propType)).map(g => (
        <div key={g.title} style={S.group}>
          <div style={S.groupTitle}>{g.title}</div>
          {g.hint && <div style={S.hint}>{g.hint}</div>}
          <div style={S.grid}>
            {/* Per-FIELD showFor as well as per-group: a few groups (Add-ons) are relevant to both
                proposal types but contain one or two fields that are not, and splitting them into
                near-duplicate groups would reorder the Basic Services editor for no reason. */}
            {g.fields.filter(f => f.key !== 'Fee Proposal Type' && (!f.showFor || f.showFor.includes(propType))).map(f => {
              const lock = sums[f.key] && sums[f.key].count > 0 ? sums[f.key] : null
              const out = g.excludable && excluded.has(f.key)
              return (
                <div key={f.key} style={f.full ? S.fieldFull : S.field}>
                  {lock
                    ? <TakeoffLockedField f={f} lock={lock} />
                    : <Field f={f} value={vals[f.key]} onChange={v => set(f.key, v)} feeModels={feeModels} computedRaw={computed && computed.raw} allVals={vals} />}
                  {g.excludable && (
                    <label style={out ? S.excludeOn : S.exclude}
                      title="Still shows on the project description, but does not count toward the FPV or the fee.">
                      <input type="checkbox" checked={out} onChange={() => toggleExcluded(f.key)} style={S.excludeBox} />
                      {out ? 'Left out of fee' : 'Leave out of fee'}
                    </label>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      ))}
    </CrmModal>

    {takeoffOpen && (
      <CrmModal title="Take off from drawings" width={560} onClose={() => setTakeoffOpen(false)}
        footer={<button style={F.btnGhost} onClick={() => setTakeoffOpen(false)}>Close</button>}>
        <div style={S.launcherHint}>
          Pick a drawing to measure on. Regions belong to this proposal; category totals sum across every drawing.
        </div>
        {drawings === null ? <div style={S.muted}>Loading…</div>
          : drawings.length === 0
            ? <div style={S.muted}>This opportunity has no drawings yet. Add them in the Drawings section of the opportunity first.</div>
            : drawings.map(d => {
              const n = regionCountByDrawing[d.id] || 0
              return (
                <button key={d.id} style={S.drawRow} onClick={() => setEditorDrawing(d)}>
                  <span style={S.drawName}>{d.name}</span>
                  {d.drawing_type && <span style={S.typeChipSm}>{drawingTypeLabel(d.drawing_type)}</span>}
                  <span style={d.scale_points_per_foot ? S.scaledChip : S.unscaledChip}>
                    {d.scale_points_per_foot ? 'Scaled' : 'Not scaled'}
                  </span>
                  <span style={S.regionCount}>{n ? `${n} region${n === 1 ? '' : 's'}` : 'No regions'}</span>
                </button>
              )
            })}
      </CrmModal>
    )}

    {editorDrawing && (
      <TakeoffEditor
        drawing={editorDrawing}
        proposalId={savedId}
        opportunityName={opportunityName || (computed && computed.projectName) || ''}
        onClose={() => { setEditorDrawing(null); refreshRegions(savedId) }}
        onDrawingUpdated={row => {
          if (!row) return
          setDrawings(list => (list || []).map(x => x.id === row.id ? row : x))
          setEditorDrawing(prev => prev && prev.id === row.id ? row : prev)
        }}
      />
    )}
    </>
  )
}

/** Locked display for a category that has takeoff regions — value comes from the
    takeoff sum (and the save path writes that sum), not manual entry. */
function TakeoffLockedField({ f, lock }) {
  return (
    <>
      <label style={S.fl}>{f.label}<span style={S.takeoffTag}>takeoff</span></label>
      <div style={S.takeoffVal}
        title="This value is measured from takeoff regions and is locked. Delete the category's regions in the takeoff editor to enter it manually.">
        <span style={{ ...S.catDot, background: categoryColor(f.key) }} />
        <span>{fmtValue(lock.kind, lock.total)} · {lock.count} region{lock.count === 1 ? '' : 's'}</span>
      </div>
    </>
  )
}

function RO({ label, v }) { return <div><div style={S.roLabel}>{label}</div><div style={S.roVal}>{v}</div></div> }

function formatComputed(v) {
  if (v === null || v === undefined || v === '') return 'Computed after save'
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  if (Array.isArray(v)) return v.join(', ') || 'Computed after save'
  return String(v)
}

/** Textarea that grows to fit its content instead of scrolling internally — height is recalculated
    on every value change (typing, or a different record loading in) so it never needs manual drag. */
function AutoGrowTextarea({ value, onChange }) {
  const ref = useRef(null)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = el.scrollHeight + 'px'
  }, [value])
  return <textarea ref={ref} style={S.textarea} value={value} onChange={e => onChange(e.target.value)} rows={1} />
}

function Field({ f, value, onChange, feeModels, computedRaw, allVals }) {
  const label = <label style={S.fl}>{f.label}{f.type === 'cur' ? ' ($)' : ''}{f.type === 'pct' ? ' (%)' : ''}{f.type === 'computed' ? ' (auto)' : ''}</label>
  if (f.type === 'computed') {
    // Percent Alter Occupiable Heavy = 1 - Light - Moderate (Airtable formula, mirrored here) so
    // it updates live as Light/Moderate are typed instead of only after a save/reread — the
    // computed.raw value is stale until the next write-then-reread round trip.
    if (f.key === 'Percent Alter Occupiable Heavy' && allVals) {
      const light = Number(allVals['Percent Alter Occupiable Light']) || 0
      const moderate = Number(allVals['Percent Alter Occupiable Moderate']) || 0
      const heavy = 100 - light - moderate
      return <>{label}<div style={S.computedVal}>{Math.round(heavy * 100) / 100}%</div></>
    }
    // A computed field can say what sets it (`note`, keyed by proposal type), so it doesn't just
    // look locked for no reason.
    const note = f.note && f.note[(allVals && allVals['Fee Proposal Type']) || 'Basic Services']
    // `display` maps a stored value to what the team reads (Fee to Use's FRIENDS → Industry Partner).
    const raw = computedRaw ? computedRaw[f.key] : undefined
    const shown = (f.display && f.display[raw]) || formatComputed(raw)
    return <>{label}<div style={S.computedVal}>{shown}</div>{note && <div style={S.computedNote}>{note}</div>}</>
  }
  if (f.type === 'bool') {
    return <label style={S.checkRow}><input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} /> {f.label}</label>
  }
  if (f.type === 'longtext') {
    return <>{label}<AutoGrowTextarea value={value || ''} onChange={onChange} /></>
  }
  if (f.type === 'select' || f.type === 'yesno') {
    const opts = f.type === 'yesno' ? ['', 'Yes', 'No'] : ['', ...(f.options || [])]
    return <>{label}<select style={S.input} value={value || ''} onChange={e => onChange(e.target.value)}>{opts.map(o => <option key={o} value={o}>{o || '—'}</option>)}</select></>
  }
  if (f.type === 'feemodel') {
    return <>{label}<select style={S.input} value={value || ''} onChange={e => onChange(e.target.value)}><option value="">—</option>{feeModels.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}</select></>
  }
  if (f.type === 'multiselect') {
    const sel = Array.isArray(value) ? value : []
    return <>{label}<div style={S.multi}>{(f.options || []).map(o => {
      const on = sel.includes(o)
      return <button key={o} type="button" style={on ? S.chipOn : S.chip} onClick={() => onChange(on ? sel.filter(x => x !== o) : [...sel, o])}>{o}</button>
    })}</div></>
  }
  if (f.type === 'contact') {
    return <>{label}<ContactPicker value={value} onChange={onChange} /></>
  }
  // num / cur / pct / date / text
  const inputType = f.type === 'date' ? 'date' : (f.type === 'num' || f.type === 'cur' || f.type === 'pct') ? 'number' : 'text'
  return <>{label}<input style={S.input} type={inputType} step="any" value={value ?? ''} onChange={e => onChange(e.target.value)} /></>
}

function ContactPicker({ value, onChange }) {
  const [q, setQ] = useState('')
  const [results, setResults] = useState(null)
  const [picked, setPicked] = useState(value ? { id: value, name: '' } : null)
  const debounceRef = useRef(null)

  // Sync picked.id with the value prop (e.g. reopening a proposal with an already-saved signer) —
  // preserves the name already on hand for the same id (set instantly by the search-select handler
  // below) rather than blanking it out.
  useEffect(() => {
    setPicked(prev => {
      if (!value) return null
      return (prev && prev.id === value) ? prev : { id: value, name: '' }
    })
  }, [value])

  // Resolve the display name for a picked contact that doesn't have one yet — only the "loaded
  // with an existing id" path hits this; a fresh pick from search already carries its name.
  useEffect(() => {
    if (!picked || picked.name || !picked.id) return
    let cancelled = false
    getContactNamesByIds([picked.id]).then(names => {
      if (cancelled) return
      const n = names[picked.id]
      if (n) setPicked(p => (p && p.id === picked.id) ? { ...p, name: n } : p)
    })
    return () => { cancelled = true }
  }, [picked])

  // Debounced (matches ContactsLauncher.jsx) — firing a search request on every keystroke was
  // enough to trip Airtable's ~5 req/sec per-base rate limit on a normal typing speed, surfacing
  // as an opaque 500 from /api/contacts.
  function search(v) {
    setQ(v)
    clearTimeout(debounceRef.current)
    if (!v.trim()) { setResults(null); return }
    debounceRef.current = setTimeout(async () => {
      try { const r = await searchContacts(v); setResults((r.contacts || []).slice(0, 6)) }
      catch { setResults([]) }
    }, 350)
  }
  if (picked) return (
    <div style={S.pickedRow}><span style={S.pickedName}>{picked.name || 'Loading…'}</span>
      <button type="button" style={S.clearBtn} onClick={() => { setPicked(null); onChange('') }}>change</button></div>
  )
  return (
    <div style={{ position: 'relative' }}>
      <input style={S.input} placeholder="Search contacts…" value={q} onChange={e => search(e.target.value)} />
      {results && results.length > 0 && (
        <div style={S.results}>
          {results.map(r => (
            <button key={r.id} type="button" style={S.resItem} onClick={() => { const n = (r.fields['Name'] || '').trim(); setPicked({ id: r.id, name: n }); onChange(r.id); setResults(null) }}>
              {(r.fields['Name'] || r.id).trim()}{r.fields['Email'] ? <span style={S.resSub}> · {r.fields['Email']}</span> : null}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

const S = {
  typeChip: { display: 'inline-block', fontFamily: '"Montserrat", sans-serif', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#1a6fa8', background: 'rgba(26,111,168,0.1)', borderRadius: 20, padding: '4px 12px', marginBottom: 14 },
  seedNote: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-subtle)', borderRadius: 6, padding: '9px 12px', marginBottom: 14, lineHeight: 1.5 },
  readout: { background: 'rgba(29,158,117,0.06)', border: '0.5px solid rgba(29,158,117,0.25)', borderRadius: 8, padding: '12px 14px', marginBottom: 16 },
  readoutHead: { fontFamily: '"Montserrat", sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#1f9d57', marginBottom: 8 },
  readoutGrid: { display: 'flex', flexWrap: 'wrap', gap: 22 },
  roLabel: { fontFamily: '"DM Sans", sans-serif', fontSize: 10, color: 'var(--aj-text-mid)' },
  roVal: { fontFamily: '"DM Mono", monospace', fontSize: 13, fontWeight: 600, color: 'var(--aj-text)', marginTop: 2 },
  group: { marginBottom: 16, borderTop: '0.5px solid var(--aj-border)', paddingTop: 14 },
  groupTitle: { fontFamily: '"Montserrat", sans-serif', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', marginBottom: 4 },
  hint: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', marginBottom: 10 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 },
  field: {}, fieldFull: { gridColumn: '1 / -1' },
  fl: { display: 'block', fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, fontWeight: 500, color: 'var(--aj-text-mid)', marginBottom: 4 },
  input: { width: '100%', fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text)', padding: '6px 8px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-elevated)', boxSizing: 'border-box' },
  computedVal: { width: '100%', fontFamily: '"DM Mono", monospace', fontSize: 12.5, color: 'var(--aj-text-mid)', padding: '6px 8px', border: '0.5px solid var(--aj-border)', borderRadius: 4, background: 'var(--aj-bg-muted)', boxSizing: 'border-box' },
  computedNote: { fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, color: 'var(--aj-text-muted)', marginTop: 4, lineHeight: 1.4 },
  textarea: { width: '100%', fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text)', padding: '7px 9px', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, background: 'var(--aj-bg-elevated)', boxSizing: 'border-box', minHeight: 64, resize: 'none', overflow: 'hidden' },
  checkRow: { display: 'flex', alignItems: 'center', gap: 7, fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text)', cursor: 'pointer', paddingTop: 16 },
  multi: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  chip: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-muted)', border: 'none', borderRadius: 16, padding: '5px 11px', cursor: 'pointer' },
  chipOn: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: '#fff', background: '#07272D', border: 'none', borderRadius: 16, padding: '5px 11px', cursor: 'pointer' },
  results: { position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 5, background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 6, marginTop: 2, boxShadow: '0 6px 18px rgba(0,0,0,0.15)', overflow: 'hidden' },
  resItem: { display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderBottom: '0.5px solid var(--aj-border)', padding: '8px 10px', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text)', cursor: 'pointer' },
  resSub: { color: 'var(--aj-text-mid)', fontSize: 11 },
  pickedRow: { display: 'flex', alignItems: 'center', gap: 8 },
  pickedName: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  clearBtn: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: '#1a6fa8', background: 'none', border: 'none', cursor: 'pointer' },
  // Takeoff-locked field (visually distinct from 'computed' — blue tint + category dot + tag)
  takeoffTag: { fontFamily: '"DM Mono", monospace', fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#1a6fa8', background: 'rgba(26,111,168,0.1)', borderRadius: 3, padding: '1px 5px', marginLeft: 6, verticalAlign: 'middle' },
  takeoffVal: { display: 'flex', alignItems: 'center', gap: 7, width: '100%', fontFamily: '"DM Mono", monospace', fontSize: 12.5, color: 'var(--aj-text)', padding: '6px 8px', border: '0.5px solid rgba(26,111,168,0.35)', borderRadius: 4, background: 'rgba(26,111,168,0.06)', boxSizing: 'border-box', cursor: 'default' },
  catDot: { width: 9, height: 9, borderRadius: '50%', flexShrink: 0 },
  // "Leave out of fee" under each area metric (database/388)
  exclude: { display: 'flex', alignItems: 'center', gap: 5, marginTop: 4, fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, color: 'var(--aj-text-muted)', cursor: 'pointer' },
  excludeOn: { display: 'flex', alignItems: 'center', gap: 5, marginTop: 4, fontFamily: '"DM Sans", sans-serif', fontSize: 10.5, fontWeight: 600, color: '#b5651d', cursor: 'pointer' },
  excludeBox: { margin: 0, width: 12, height: 12, cursor: 'pointer' },
  // Take-off launcher (drawing rows mirror CrmDrawingsSection chips)
  launcherHint: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', marginBottom: 8, lineHeight: 1.5 },
  muted: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', fontStyle: 'italic', padding: '4px 0' },
  drawRow: { display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: 'none', border: 'none', borderBottom: '0.5px solid var(--aj-border)', padding: '9px 0', cursor: 'pointer', textAlign: 'left' },
  drawName: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text)' },
  typeChipSm: { fontFamily: '"DM Mono", monospace', fontSize: 10, color: 'var(--aj-text-mid)', background: 'var(--aj-bg-muted)', borderRadius: 4, padding: '2px 6px', flexShrink: 0 },
  scaledChip: { fontFamily: '"DM Mono", monospace', fontSize: 10, color: '#1a7f4e', background: 'rgba(26,127,78,0.12)', borderRadius: 4, padding: '2px 6px', flexShrink: 0 },
  unscaledChip: { fontFamily: '"DM Mono", monospace', fontSize: 10, color: 'var(--aj-text-muted)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 4, padding: '2px 6px', flexShrink: 0 },
  regionCount: { fontFamily: '"DM Mono", monospace', fontSize: 10.5, color: 'var(--aj-text-mid)', flexShrink: 0, width: 78, textAlign: 'right' },
}
